import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiLoader,
  FiThumbsUp,
  FiAlertTriangle,
} from 'react-icons/fi';
import { useReviewerContext } from '../../context/ReviewerContext';

const filterOptions = ['All', 'Completed', 'Processing', 'Pending'];

const ReviewerQuestionnairesHeader = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  
  // Get everything from context
  const {
    questionnaires,
    questionsMap,
    activeQnr,
    activeFilter,
    searchTerm,
    setSearchTerm,
    handleFilterChange,
    handleSwitchQnr,
    acceptanceStatus,
    reviewerAccepting,
    handleReviewerAccept,
  } = useReviewerContext();

  const scrollQnr = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 180, behavior: 'smooth' });
    }
  };

  const filteredQnrs = questionnaires.filter((qnr) => {
    if (activeFilter === 'All') return true;
    return qnr.status === activeFilter.toLowerCase();
  });

  return (
    <div className=" p-5 bg-white cardStyle">
      <div className="flex items-center justify-between mb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-[#0077b5]">Questionnaires</h2>
          <nav className="text-sm mt-1 text-[#919191]">
            <button
              onClick={() => navigate('/reviewer/assessments')}
              className="text-[#0077b5] hover:underline font-medium p-0 mr-2"
            >
              Assessments
            </button>
            <span className="text-[#919191]">&gt;</span>
            <span className="ml-2 text-[#919191] font-medium">Questionnaires</span>
          </nav>
        </div>
        <div className="flex items-center gap-3">
          {/* Filter Dropdown */}
          <div className="relative">
            <div className="flex items-center gap-1 border border-[#919191] px-3 py-2 text-sm text-[#919191] cursor-pointer">
              <FiFilter className="text-[#919191]" />
              <select
                value={activeFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="bg-transparent border-none outline-none text-sm text-[#919191] cursor-pointer"
              >
                {filterOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Search */}
          <div className="flex items-center border border-[#919191] px-3 py-2">
            <FiSearch className="text-[#919191] mr-2" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-40 text-[#919191] placeholder-[#919191]"
            />
          </div>
        </div>
      </div>

      

      {/* Scrollable questionnaire cards */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => scrollQnr(-1)}
          className="p-1 rounded-full hover:bg-[#919191]/10 text-[#919191] flex-shrink-0"
        >
          <FiChevronLeft />
        </button>
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide flex-1"
          style={{ scrollbarWidth: 'none' }}
        >
          {filteredQnrs.map((qnr) => {
            const isActive = qnr.id === activeQnr;
            const answered = qnr.progress.answered || 0;
            const total = qnr.progress.total || 0;
            const isCompleted = total > 0 && answered >= total;
            const statusLabel = isCompleted ? 'Completed' : 'Progress';
            const progressPct = total > 0 ? Math.min((answered / total) * 100, 100) : 0;

            const qnrQuestions = questionsMap[qnr.id] || [];
            const reviewedCount = qnrQuestions.filter(q => q.reviewStatus !== null).length;
            const reviewTotal = qnrQuestions.length;
            const reviewPct = reviewTotal > 0 ? Math.min(Math.round((reviewedCount / reviewTotal) * 100), 100) : 0;

            return (
              <button
                key={qnr.id}
                onClick={() => handleSwitchQnr(qnr.id)}
                className={`min-w-[170px] max-w-[190px] flex-shrink-0 border-2 p-3 text-left transition-all ${
                  isActive
                    ? 'border-accent-400 bg-accent-50 shadow-md'
                    : 'border-blue-200 bg-white hover:border-blue-400 hover:shadow-sm'
                }`}
              >
                {/* Dot + name */}
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                      isActive ? 'bg-accent-400' : 'bg-blue-500'
                    }`}
                  ></span>
                  <span
                    className={`text-xs font-semibold truncate ${
                      isActive ? 'text-accent-500' : 'text-blue-900'
                    }`}
                  >
                    {qnr.name}
                  </span>
                </div>
                {/* Answered bar - commented out */}
                {/* <div className="mt-1 space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-medium text-blue-900">Answered</span>
                    <span className="text-[9px] font-semibold text-blue-900">{Math.round(progressPct)}%</span>
                  </div>
                  <div className="h-[5px] bg-blue-50 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all bg-blue-700"
                      style={{ width: `${progressPct}%` }}
                    ></div>
                  </div>
                </div> */}
                {/* Reviewed bar */}
                <div className="mt-1.5 space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-medium text-indigo-500">Reviewed</span>
                    <span className="text-[9px] font-semibold text-indigo-500">{reviewPct}%</span>
                  </div>
                  <div className="h-[5px] bg-blue-50 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all bg-indigo-500"
                      style={{ width: `${reviewPct}%` }}
                    ></div>
                  </div>
                </div>
                {/* Date
                {qnr.updatedAt && (
                  <p className="text-[9px] text-blue-300 mt-1.5">
                    {qnr.updatedAt}
                  </p>
                )} */}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => scrollQnr(1)}
          className="p-1 rounded-full hover:bg-[#919191]/10 text-[#919191] flex-shrink-0"
        >
          <FiChevronRight />
        </button>
      </div>

      {/* Subtitle */}
      <p className="text-sm text-[#919191] mt-3">Select a questionnaire card to review its questions, submitted responses, and supporting evidence. Validate the responses, record any review comments or findings, and repeat the process for each questionnaire. Once all questionnaires have been successfully reviewed, accept the assessment to complete the review.</p>

      {/* ─── Reviewer Acceptance ─── */}
      <div className="mt-2 pt-4 border-t border-[#919191]/20 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Reviewer status pill */}
          <span className={`flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${
            acceptanceStatus.reviewerAccepted
              ? 'text-[#0077b5] bg-[#e8f4fc] border border-[#0077b5]/30'
              : 'text-[#919191] bg-[#f4f4f4] border border-[#919191]/20'
          }`}>
            <span className={`w-2 h-2 rounded-full ${acceptanceStatus.reviewerAccepted ? 'bg-[#0077b5]' : 'bg-[#d1d5db]'}`} />
            Reviewer: {acceptanceStatus.reviewerAccepted ? 'Accepted' : 'Pending'}
          </span>

          {/* User status pill */}
          <span className={`flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${
            acceptanceStatus.userRejected
              ? 'text-red-600 bg-red-50 border border-red-300'
              : acceptanceStatus.userAccepted
                ? 'text-[#F47F2E] bg-[#fff5ee] border border-[#F47F2E]/30'
                : 'text-[#919191] bg-[#f4f4f4] border border-[#919191]/20'
          }`}>
            {acceptanceStatus.userRejected ? (
              <FiAlertTriangle className="text-xs" />
            ) : (
              <span className={`w-2 h-2 rounded-full ${acceptanceStatus.userAccepted ? 'bg-[#F47F2E]' : 'bg-[#d1d5db]'}`} />
            )}
            User: {acceptanceStatus.userRejected ? 'Need more information' : acceptanceStatus.userAccepted ? 'Accepted' : 'Pending'}
          </span>

          {acceptanceStatus.bothAccepted && (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-full px-3 py-1">
              <FiCheckCircle className="text-sm" /> Both Accepted
            </span>
          )}
          {/* {acceptanceStatus.userRejected && !acceptanceStatus.reviewerAccepted && (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-3 py-1">
              <FiAlertTriangle className="text-xs" /> Re-review required
            </span>
          )} */}
        </div>

        {/* Accept button — always visible */}
        <button
          onClick={handleReviewerAccept}
          disabled={reviewerAccepting}
          className="flex items-center gap-2 px-5 py-2 bg-[#ed7d2d] text-white text-xs font-semibold shadow-sm hover:bg-[#ed7d2d] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex-shrink-0"
        >
          {reviewerAccepting ? (
            <FiLoader className="animate-spin text-sm" />
          ) : (
            <FiThumbsUp className="text-sm" />
          )}
          {reviewerAccepting ? 'Accepting...' : 'Accept Assessment'}
        </button>
      </div>
    </div>
  );
};

export default ReviewerQuestionnairesHeader;
